import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import moment from 'moment';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { Endorsement, FormDate, FormInput } from '@sdir/blueprint.aps/lib/components';
import { Loader, SdirButton } from '@sdir/sds';
// eslint-disable-next-line max-len
import { EndorsementServiceFeaturesVerifyEndorsementVerifyEndorsementDto as EndorsementDto } from '@sdir/httpclient/lib/clients/aps/endorsement';
import { endorsementApi } from '../../../httpclient';
import { messages } from '../../../localization';

const maxDate = new Date(2500, 11, 31);

const SearchEndorsementPage: React.FC = () => {
  const intl = useIntl();
  const [searchError, setSearchError] = useState<any>(null);
  const [endorsement, setEndorsement] = useState<EndorsementDto>();
  const [loading, setLoading] = useState<boolean>(false);

  const initialValues = {
    dateOfBirth: '',
    endorsementNumber: '',
  };

  const handleSubmit = useCallback(
    (values) => {
      const shortDateOfBirth = values?.dateOfBirth
        ? moment(values?.dateOfBirth).format('YYYY-MM-DD')
        : '';
      setLoading(true);
      endorsementApi
        .verifyEndorsementBybirthdate(values?.endorsementNumber, shortDateOfBirth)
        .then((response) => {
          if (response.status === 200) {
            setEndorsement(response.data);
            setSearchError(undefined);
          }
          setLoading(false);
        })
        .catch((error) => {
          const { status } = error?.response || {};
          if (status >= 400 && status < 500) {
            setSearchError(intl.formatMessage({ id: 'endorsement.notfound' }));
          } else {
            setSearchError(error);
          }
          setLoading(false);
        });
    },
    [intl]
  );

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      dateOfBirth: Yup.string().required(intl.formatMessage({ id: 'required.field' })),
      endorsementNumber: Yup.string().required(intl.formatMessage({ id: 'required.field' })),
    });
  }, [intl]);

  return (
    <>
      <h1>{intl.formatMessage({ id: 'endorsement.search.header' })}</h1>
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {() => (
          <StyledForm>
            <Field
              label={intl.formatMessage({ id: 'birthnumber' })}
              name="dateOfBirth"
              component={FormDate}
              locale={intl?.locale || 'no'}
              format="dd.MM.yyyy"
              maxDate={maxDate}
              showYearDropdown
              showMonthDropdown
            />
            <Field
              label={intl.formatMessage({ id: 'endorsement.number' })}
              name="endorsementNumber"
              component={FormInput}
            />
            <StyledSdirButton
              htmlType="submit"
              type="primary"
              text={intl.formatMessage({ id: 'search' })}
            />
            {loading && <Loader />}
            {searchError && <ErrorMessage error={searchError} />}
          </StyledForm>
        )}
      </Formik>
      {endorsement && !searchError && (
        <ResultWrapper translate="no">
          <Endorsement
            authority={endorsement?.authority || ''}
            issuingAuthority={endorsement?.issuingAuthority}
            certificateNr={endorsement?.certificateNumber || ''}
            endorsementNr={endorsement?.endorsementNumber || ''}
            dateOfBirth={endorsement?.seafarerDateOfBirth || ''}
            name={endorsement?.seafarerName || ''}
            defaultLang="en"
            positionList={[
              {
                position: endorsement?.title || '',
                limitations: [messages?.en?.['endorsement.limitation']],
              },
            ]}
            validDate={endorsement?.expiresAt || ''}
            isStcwF={endorsement?.regulationRefrence === 'STCW_F'}
            issuedDate={endorsement?.issuedAt}
            hideQr
            qrImageLoading={false}
            // eslint-disable-next-line max-len
            qrData={`${process.env.REACT_APP_VERIFY_URL}/endorsement/${endorsement?.certificateNumber}`}
            hideLanguageSelector
          />
        </ResultWrapper>
      )}
    </>
  );
};

const StyledForm = styled(Form)`
  display: flex;
  grid-row-gap: 1em;
  flex-direction: column;
  max-width: 400px;
`;

const StyledSdirButton = styled(SdirButton)`
  width: fit-content;
`;

const ResultWrapper = styled.div`
  margin: 20px 0;

  p {
    margin: 0;
  }
`;

export default SearchEndorsementPage;
